import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Autocomplete,
  Switch,
  FormControlLabel,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Backdrop,
  CircularProgress,
  Checkbox,
  Snackbar,
  Alert,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { colors, defaultInputSX } from '@src/styles';
import type { LutienOrganizationsGetResponseDTO } from '@src/services/lutien/modules/v1/organizations/dto/lutien-organizations.get.response.dto';
import type { OrgConfigGetResponseDTO } from '@src/services/org-config/dto';
import type { HubExtensionsGetByIdResponseDTO } from '@src/services/hub/modules/v1/extensions/dto/hub-extensions.getById.response.dto';

const allowedStatusesMock = ['CONCLUDED', 'REJECTED'] as const;

const titleFontFamily = "'Inter Tight', 'Inter', sans-serif";

const switchSx = {
  '& .MuiSwitch-switchBase.Mui-checked': { color: colors.primary.yellow },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: colors.primary.yellow, opacity: 0.45 },
  '& .MuiSwitch-track': { backgroundColor: colors.brand.textMuted },
} as const;

const toggleLabelSx = {
  marginLeft: 0,
  gap: 0.5,
  '& .MuiFormControlLabel-label': { fontFamily: titleFontFamily, fontSize: '13.5px', color: colors.brand.textStrong },
} as const;

const chipSx = {
  height: 24,
  borderRadius: '999px',
  backgroundColor: 'rgba(255, 160, 21, 0.1)',
  borderColor: 'rgba(255, 160, 21, 0.45)',
  color: colors.primary.yellow,
  fontSize: '12px',
  '& .MuiChip-label': { color: colors.primary.yellow },
  '& .MuiChip-deleteIcon': { color: colors.primary.yellow, opacity: 0.7, '&:hover': { opacity: 1 } },
  '&:hover': { backgroundColor: 'rgba(255, 160, 21, 0.16)' },
} as const;

const dropdownSlotProps = {
  paper: {
    sx: {
      marginTop: 0.5,
      backgroundColor: colors.brand.surface,
      backgroundImage: 'none',
      border: `1px solid ${colors.brand.line}`,
      borderRadius: '10px',
      boxShadow: '0 16px 40px rgba(0, 0, 0, 0.45)',
    },
  },
  listbox: {
    sx: {
      padding: 0.5,
      '& .MuiAutocomplete-option': {
        borderRadius: '6px',
        color: colors.primary.white,
        fontSize: '13.5px',
        '&.Mui-focused': { backgroundColor: 'rgba(255, 255, 255, 0.05)' },
        '&[aria-selected="true"]': { backgroundColor: 'rgba(255, 160, 21, 0.12)' },
        '&[aria-selected="true"].Mui-focused': { backgroundColor: 'rgba(255, 160, 21, 0.18)' },
      },
    },
  },
} as const;

const primaryButtonSx = {
  height: 38,
  paddingX: 2.25,
  borderRadius: '8px',
  backgroundColor: colors.primary.yellow,
  color: colors.brand.ink,
  fontFamily: titleFontFamily,
  fontSize: '13.5px',
  fontWeight: 600,
  textTransform: 'none',
  boxShadow: 'none',
  '&:hover': { backgroundColor: '#FFB94A', boxShadow: 'none' },
  '&.Mui-disabled': { backgroundColor: 'rgba(255, 160, 21, 0.25)', color: 'rgba(10, 13, 15, 0.6)' },
} as const;

const secondaryButtonSx = {
  height: 38,
  paddingX: 2,
  borderRadius: '8px',
  border: '1px solid rgba(255, 160, 21, 0.5)',
  backgroundColor: 'rgba(255, 160, 21, 0.1)',
  color: colors.primary.yellow,
  fontFamily: titleFontFamily,
  fontSize: '13.5px',
  fontWeight: 600,
  textTransform: 'none',
  '&:hover': { backgroundColor: 'rgba(255, 160, 21, 0.18)', borderColor: colors.primary.yellow },
} as const;

const ghostButtonSx = {
  height: 30,
  paddingX: 1.25,
  borderRadius: '8px',
  border: `1px solid ${colors.brand.line}`,
  color: colors.brand.textStrong,
  fontFamily: titleFontFamily,
  fontSize: '12.5px',
  fontWeight: 500,
  textTransform: 'none',
  '&:hover': { borderColor: colors.primary.yellow, backgroundColor: 'transparent' },
} as const;

const dialogPaperProps = {
  sx: {
    backgroundColor: colors.brand.surface,
    backgroundImage: 'none',
    border: `1px solid ${colors.brand.line}`,
    borderRadius: '14px',
    color: colors.primary.white,
    boxShadow: '0 30px 80px rgba(0, 0, 0, 0.45)',
    '& .MuiDialogTitle-root': {
      fontFamily: titleFontFamily,
      fontSize: '17px',
      fontWeight: 600,
      paddingBottom: 1,
    },
    '& .MuiDialogContent-root .MuiTypography-root': {
      fontSize: '13.5px',
      color: colors.brand.textSecondary,
      lineHeight: 1.55,
    },
    '& .MuiDialogActions-root': { padding: '8px 16px 16px' },
    '& .MuiDialogActions-root .MuiButton-root': ghostButtonSx,
  },
} as const;

const dialogBackdropProps = {
  backdrop: { sx: { backdropFilter: 'blur(6px)', backgroundColor: 'rgba(10, 13, 15, 0.55)' } },
} as const;

export interface ExtensionOption {
  id: string;
  label: string;
  data?: HubExtensionsGetByIdResponseDTO;
}

export type OrgConfigViewProps = {
  organizations: LutienOrganizationsGetResponseDTO;
  isLoadingOrgs: boolean;
  existingConfig: OrgConfigGetResponseDTO | null | undefined;
  isLoadingConfig: boolean;
  extensionOptions: ExtensionOption[];
  isLoadingExtensions?: boolean;
  isTogglingExtension?: boolean;
  notConfiguredOpen?: boolean;
  onCloseNotConfigured?: () => void;
  isSaving?: boolean;
  saveSuccessOpen?: boolean;
  onCloseSaveSuccess?: () => void;
  saveErrorOpen?: boolean;
  saveErrorMessage?: string;
  onCloseSaveError?: () => void;
  extensionToastOpen?: boolean;
  extensionToastMessage?: string;
  extensionToastSeverity?: 'success' | 'error';
  onCloseExtensionToast?: () => void;
  onSelectOrg: (id: string) => void;
  onSubmit: (form: OrgConfigForm) => void;
  onGenerateToken: () => string;
  onGoToMapping?: (organizationId: string) => void;
  onExtensionsChange?: (extensions: ExtensionOption[]) => Promise<void>;
};

export type OrgConfigForm = {
  organizationId: string;
  token: string;
  aliareUrl?: string;
  aliareUser?: string;
  aliareToken?: string;
  aliareCode?: string;
  withAssets: boolean;
  targetPhaseId?: string;
  organizationName: string;
  considerCorporateName: boolean;
  defaultAssignedUserId?: string;
  aliareCycleUrl?: string;
  invisibleCompanyBranch: boolean;
  withCycles: boolean;
  allowedCreditLimitStatus: string[];
};

export const OrgConfigView = ({
  organizations,
  isLoadingOrgs,
  existingConfig,
  isLoadingConfig,
  extensionOptions,
  isLoadingExtensions = false,
  isTogglingExtension = false,
  notConfiguredOpen = false,
  onCloseNotConfigured,
  isSaving = false,
  saveSuccessOpen = false,
  onCloseSaveSuccess,
  saveErrorOpen = false,
  saveErrorMessage,
  onCloseSaveError,
  extensionToastOpen = false,
  extensionToastMessage,
  extensionToastSeverity = 'success',
  onCloseExtensionToast,
  onSelectOrg,
  onSubmit,
  onGenerateToken,
  onGoToMapping,
  onExtensionsChange,
}: OrgConfigViewProps) => {
  const [form, setForm] = useState<OrgConfigForm>(() => ({
    organizationId: existingConfig?.organizationId || '',
    token: existingConfig?.token || '',
    aliareUrl: existingConfig?.aliareUrl || '',
    aliareUser: existingConfig?.aliareUser || '',
    aliareToken: existingConfig?.aliareToken || '',
    aliareCode: existingConfig?.aliareCode || '',
    withAssets: existingConfig?.withAssets || false,
    targetPhaseId: existingConfig?.targetPhaseId || '',
    organizationName: existingConfig?.organizationName || '',
    considerCorporateName: existingConfig?.considerCorporateName ?? false,
    defaultAssignedUserId: existingConfig?.defaultAssignedUserId || '',
    aliareCycleUrl: existingConfig?.aliareCycleUrl || '',
    invisibleCompanyBranch: existingConfig?.invisibleCompanyBranch || false,
    withCycles: existingConfig?.withCycles || false,
    allowedCreditLimitStatus: existingConfig?.allowedCreditLimitStatus || [],
  }));

  const selectedOrg = useMemo(
    () => organizations.find((o) => o.id === form.organizationId) || null,
    [organizations, form.organizationId],
  );

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedExtensions, setSelectedExtensions] = useState<ExtensionOption[]>([]);
  const [pendingExtensions, setPendingExtensions] = useState<ExtensionOption[]>([]);
  const [isProcessingExtensions, setIsProcessingExtensions] = useState(false);

  useEffect(() => {
    if (!existingConfig) return;
    setForm((prev) => ({
      ...prev,
      organizationId: existingConfig.organizationId || prev.organizationId,
      token: existingConfig.token || '',
      aliareUrl: existingConfig.aliareUrl || '',
      aliareUser: existingConfig.aliareUser || '',
      aliareToken: existingConfig.aliareToken || '',
      aliareCode: existingConfig.aliareCode || '',
      withAssets: existingConfig.withAssets || false,
      targetPhaseId: existingConfig.targetPhaseId || '',
      organizationName: existingConfig.organizationName || prev.organizationName,
      considerCorporateName: existingConfig.considerCorporateName ?? false,
      defaultAssignedUserId: existingConfig.defaultAssignedUserId || '',
      aliareCycleUrl: existingConfig.aliareCycleUrl || '',
      invisibleCompanyBranch: existingConfig.invisibleCompanyBranch || false,
      withCycles: existingConfig.withCycles || false,
      allowedCreditLimitStatus: existingConfig.allowedCreditLimitStatus || [],
    }));
  }, [existingConfig]);

  const allowedStatusesOptions = useMemo(
    () =>
      Array.from(
        new Set([
          ...(allowedStatusesMock as unknown as string[]),
          ...((form.allowedCreditLimitStatus || []) as string[]),
        ]),
      ),
    [form.allowedCreditLimitStatus],
  );

  useEffect(() => {
    if (!notConfiguredOpen) return;
    setForm((f) => ({
      ...f,
      token: '',
      aliareUrl: '',
      aliareUser: '',
      aliareToken: '',
      aliareCode: '',
      withAssets: false,
      targetPhaseId: '',
      considerCorporateName: false,
      defaultAssignedUserId: '',
      aliareCycleUrl: '',
      invisibleCompanyBranch: false,
      withCycles: false,
      allowedCreditLimitStatus: [],
    }));
  }, [notConfiguredOpen]);

  useEffect(() => {
    if (!form.organizationId || !extensionOptions.length) {
      setSelectedExtensions([]);
      setPendingExtensions([]);
      return;
    }

    const activeExtensions = extensionOptions.filter((ext) => {
      if (!ext.data?.subscribedOrganizationIds) return false;
      return ext.data.subscribedOrganizationIds.includes(form.organizationId);
    });

    setSelectedExtensions(activeExtensions);
    setPendingExtensions(activeExtensions);
  }, [form.organizationId, extensionOptions]);

  const inputSx = {
    ...defaultInputSX,
    marginBottom: 0,
    '& .MuiOutlinedInput-root': {
      ...defaultInputSX['& .MuiOutlinedInput-root'],
      backgroundColor: colors.background.primary,
      borderRadius: '8px',
      fontSize: '13.5px',
      '& fieldset': { borderColor: colors.brand.line },
      '&:hover fieldset': { borderColor: 'rgba(255, 160, 21, 0.5)' },
      '&.Mui-focused fieldset': { borderColor: colors.primary.yellow },
      '&.Mui-error fieldset': { borderColor: colors.status.rejected },
    },
    '& .MuiInputBase-input': { color: colors.primary.white },
    '& .MuiInputBase-input[readonly]': { color: colors.brand.textSecondary },
    '& .MuiInputLabel-root': {
      color: colors.brand.textSecondary,
      fontSize: '13.5px',
      '&.Mui-focused': { color: colors.primary.yellow },
      '&.Mui-error': { color: colors.status.rejected },
    },
    '& .MuiFormHelperText-root': { marginLeft: '2px', color: colors.status.rejected, fontSize: '11.5px' },
    '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': { color: colors.brand.textSecondary },
    '& .MuiCircularProgress-root': { color: colors.primary.yellow },
  } as const;

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!form.organizationId) {
      newErrors.organization_id = 'Obrigatório';
    }
    if (!form.token) {
      newErrors.token = 'Obrigatório';
    }
    if (form.withAssets && !form.aliareUrl) {
      newErrors.aliare_url = 'Obrigatório quando Crédito Rotativo = true';
    }
    if (form.withCycles && !form.aliareCycleUrl) {
      newErrors.aliare_cycle_url = 'Obrigatório quando Crédito por Ciclos = true';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    onSubmit({
      ...form,
      organizationName: selectedOrg?.fantasyName || form.organizationName,
    });
  };

  const handleSelectOrg = (_: unknown, value: { id?: string; fantasyName?: string } | null) => {
    const id = value?.id || '';
    setForm((f) => ({ ...f, organizationId: id, organizationName: value?.fantasyName || '' }));
    if (id) onSelectOrg(id);
  };

  const setField = (key: keyof OrgConfigForm, value: OrgConfigForm[keyof OrgConfigForm]) =>
    setForm((f) => ({ ...f, [key]: value }));

  return (
    <Box
      sx={{
        minHeight: { xs: 'auto', md: 'calc(100vh - 32px)' },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        paddingY: { xs: 1, md: 3 },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          padding: { xs: '18px 16px', md: '24px 28px' },
          gap: '8px',
          width: '100%',
          maxHeight: { md: 'calc(100vh - 80px)' },
          maxWidth: 1000,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: colors.brand.surface,
          backgroundImage: 'none',
          color: colors.primary.white,
          border: `1px solid ${colors.brand.line}`,
          borderRadius: '14px',
          boxShadow: '0 30px 80px rgba(0, 0, 0, 0.35)',
          position: 'relative',
          overflow: 'auto',
          animation: 'aliareFadeInUp 0.32s cubic-bezier(0.2, 0.7, 0.2, 1) both',
          '@keyframes aliareFadeInUp': {
            from: { opacity: 0, transform: 'translateY(8px)' },
            to: { opacity: 1, transform: 'translateY(0)' },
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            mb: 2,
            paddingBottom: 2,
            borderBottom: `1px solid ${colors.brand.line}`,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontFamily: titleFontFamily,
              fontSize: '22px',
              fontWeight: 500,
              letterSpacing: '-0.025em',
              color: colors.primary.white,
            }}
          >
            [CONTEXTO ALIARE] - Setup de Organização
          </Typography>
          <Box sx={{ minWidth: { xs: '100%', sm: 425 }, display: 'flex', alignItems: 'center' }}>
            <Autocomplete
              fullWidth
              multiple
              options={extensionOptions}
              value={pendingExtensions}
              onChange={(_, newValue) => {
                setPendingExtensions(newValue);
              }}
              onClose={async () => {
                // Comparar se houve mudanças
                const currentIds = new Set(selectedExtensions.map((e) => e.id));
                const pendingIds = new Set(pendingExtensions.map((e) => e.id));

                const hasChanges =
                  currentIds.size !== pendingIds.size || [...currentIds].some((id) => !pendingIds.has(id));

                if (hasChanges && onExtensionsChange) {
                  setIsProcessingExtensions(true);
                  try {
                    await onExtensionsChange(pendingExtensions);
                  } finally {
                    setIsProcessingExtensions(false);
                  }
                }
              }}
              disabled={!form.organizationId || isTogglingExtension || isProcessingExtensions}
              loading={isLoadingExtensions || isTogglingExtension || isProcessingExtensions}
              disableCloseOnSelect
              disableClearable
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox
                    checked={selected}
                    sx={{
                      marginRight: 1,
                      color: colors.brand.textMuted,
                      '&.Mui-checked': {
                        color: colors.primary.yellow,
                      },
                    }}
                  />
                  <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <Typography variant="body2" sx={{ color: colors.primary.white, fontSize: '13.5px' }}>
                      {option.label}
                    </Typography>
                    {option.data && (
                      <Typography variant="caption" sx={{ color: colors.brand.textMuted }}>
                        {option.data.productionManifest?.description || 'Sem descrição disponível'}
                      </Typography>
                    )}
                  </Box>
                </li>
              )}
              renderInput={(params) => <TextField {...params} label="Extensões Ativas" size="small" sx={inputSx} />}
              renderTags={(value: readonly ExtensionOption[], getTagProps) =>
                value.map((option: ExtensionOption, index: number) => {
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  const { onDelete, ...chipProps } = getTagProps({ index });
                  return <Chip {...chipProps} variant="outlined" label={option.label} sx={chipSx} />;
                })
              }
              slotProps={dropdownSlotProps}
            />
          </Box>
        </Box>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Autocomplete
              options={organizations}
              loading={isLoadingOrgs}
              getOptionLabel={(o) => o.fantasyName}
              onChange={handleSelectOrg}
              value={selectedOrg}
              slotProps={dropdownSlotProps}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Organização"
                  error={!!errors.organization_id}
                  helperText={errors.organization_id}
                  size="small"
                  sx={inputSx}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label="Id da Organização"
              value={form.organizationId}
              fullWidth
              size="small"
              error={!!errors.organization_id}
              helperText={errors.organization_id}
              InputProps={{ readOnly: true }}
              sx={inputSx}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label="API Token"
              value={form.token}
              onChange={(e) => setField('token', e.target.value)}
              fullWidth
              size="small"
              error={!!errors.token}
              helperText={errors.token}
              sx={inputSx}
            />
            <Button sx={{ ...ghostButtonSx, mt: 1 }} onClick={() => setField('token', onGenerateToken())}>
              Gerar UUID
            </Button>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label="Nome da Organização"
              value={form.organizationName}
              fullWidth
              size="small"
              InputProps={{ readOnly: true }}
              sx={inputSx}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.withAssets}
                  onChange={(e) => setField('withAssets', e.target.checked)}
                  sx={switchSx}
                />
              }
              label="Crédito Rotativo"
              sx={toggleLabelSx}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.withCycles}
                  onChange={(e) => setField('withCycles', e.target.checked)}
                  sx={switchSx}
                />
              }
              label="Crédito por Ciclos"
              sx={toggleLabelSx}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label="URL - Crédito Rotativo"
              value={form.aliareUrl}
              onChange={(e) => setField('aliareUrl', e.target.value)}
              fullWidth
              size="small"
              error={!!errors.aliare_url}
              helperText={errors.aliare_url}
              sx={inputSx}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label="URL - Crédito por Ciclos"
              value={form.aliareCycleUrl}
              onChange={(e) => setField('aliareCycleUrl', e.target.value)}
              fullWidth
              size="small"
              error={!!errors.aliare_cycle_url}
              helperText={errors.aliare_cycle_url}
              sx={inputSx}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label="Aliare User"
              value={form.aliareUser}
              onChange={(e) => setField('aliareUser', e.target.value)}
              fullWidth
              size="small"
              sx={inputSx}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label="Aliare Token"
              value={form.aliareToken}
              onChange={(e) => setField('aliareToken', e.target.value)}
              fullWidth
              size="small"
              sx={inputSx}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label="Código Aliare"
              value={form.aliareCode}
              onChange={(e) => setField('aliareCode', e.target.value)}
              fullWidth
              size="small"
              sx={inputSx}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label="Target Phase Id"
              value={form.targetPhaseId}
              onChange={(e) => setField('targetPhaseId', e.target.value)}
              fullWidth
              size="small"
              sx={inputSx}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.considerCorporateName}
                  onChange={(e) => setField('considerCorporateName', e.target.checked)}
                  sx={switchSx}
                />
              }
              label="Usar Razão Social como nome da conta"
              sx={toggleLabelSx}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label="Usuário Responsável Padrão"
              value={form.defaultAssignedUserId}
              onChange={(e) => setField('defaultAssignedUserId', e.target.value)}
              fullWidth
              size="small"
              sx={inputSx}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.invisibleCompanyBranch}
                  onChange={(e) => setField('invisibleCompanyBranch', e.target.checked)}
                  sx={switchSx}
                />
              }
              label="Filial Invisível"
              sx={toggleLabelSx}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Autocomplete
              multiple
              options={allowedStatusesOptions as unknown as string[]}
              value={form.allowedCreditLimitStatus}
              onChange={(_, value) => setField('allowedCreditLimitStatus', value as string[])}
              renderTags={(value: readonly string[], getTagProps) =>
                value.map((option: string, index: number) => (
                  <Chip {...getTagProps({ index })} variant="outlined" label={option} sx={chipSx} />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} label="Enviar para Fases de Crédito:" size="small" sx={inputSx} />
              )}
              slotProps={dropdownSlotProps}
            />
          </Grid>

          <Grid size={{ xs: 12 }} container spacing={2}>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isLoadingConfig || isSaving}
              sx={primaryButtonSx}
            >
              {existingConfig ? 'Atualizar' : 'Criar'} credenciais
            </Button>
            {existingConfig && form.organizationId && onGoToMapping && (
              <Button
                variant="outlined"
                onClick={() => onGoToMapping(form.organizationId)}
                sx={{ ...secondaryButtonSx, ml: 2 }}
              >
                Configurar Mapeamento Aliare
              </Button>
            )}
          </Grid>
        </Grid>
      </Paper>
      <Dialog
        open={!!notConfiguredOpen}
        onClose={onCloseNotConfigured}
        maxWidth="xs"
        fullWidth
        PaperProps={dialogPaperProps}
        slotProps={dialogBackdropProps}
      >
        <DialogTitle>Organização não configurada</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Não encontramos configurações para esta organização. Preencha os campos abaixo e clique em "Criar
            credenciais" para salvar.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCloseNotConfigured}>Fechar</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={!!saveSuccessOpen}
        onClose={onCloseSaveSuccess}
        maxWidth="xs"
        fullWidth
        PaperProps={dialogPaperProps}
        slotProps={dialogBackdropProps}
      >
        <DialogTitle>Credenciais salvas</DialogTitle>
        <DialogContent>
          <Typography variant="body2">As credenciais foram salvas com sucesso.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCloseSaveSuccess}>Fechar</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={!!saveErrorOpen}
        onClose={onCloseSaveError}
        maxWidth="xs"
        fullWidth
        PaperProps={dialogPaperProps}
        slotProps={dialogBackdropProps}
      >
        <DialogTitle>Erro ao salvar credenciais</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{saveErrorMessage || 'Não foi possível salvar as credenciais.'}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCloseSaveError}>Fechar</Button>
        </DialogActions>
      </Dialog>
      <Backdrop
        open={!!isLoadingConfig}
        sx={{
          color: colors.primary.yellow,
          zIndex: (theme) => theme.zIndex.modal + 1,
          backdropFilter: 'blur(6px)',
          backgroundColor: 'rgba(10, 13, 15, 0.55)',
        }}
      >
        <CircularProgress color="inherit" size={32} thickness={3} />
      </Backdrop>
      <Snackbar
        open={extensionToastOpen}
        autoHideDuration={4000}
        onClose={onCloseExtensionToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={onCloseExtensionToast} severity={extensionToastSeverity} sx={{ width: '100%' }}>
          {extensionToastMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OrgConfigView;
